const isProductionHost = typeof window !== 'undefined' && window.location.hostname.endsWith('boxed3d.com');

export const API_BASE = (import.meta as any).env?.VITE_API_URL ||
  (isProductionHost
    ? 'https://api.boxed3d.com'
    : `http://${window.location.hostname === 'localhost' ? '127.0.0.1' : (window.location.hostname || 'localhost')}:38592`);

const API_KEY = (import.meta as any).env?.VITE_API_KEY as string | undefined;

export function apiAuthHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const headers = { ...extra };
  if (API_KEY) headers.Authorization = `Bearer ${API_KEY}`;
  return headers;
}

let previewAbortController: AbortController | null = null;

export function cancelPreview(): void {
  previewAbortController?.abort();
}

export function newPreviewController(): AbortController {
  cancelPreview();
  previewAbortController = new AbortController();
  return previewAbortController;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly serverMessage: string,
    public readonly retryAfterSec: number | null,
    public readonly kind: 'busy' | 'timeout' | 'server' | 'auth' | 'client' | 'network',
  ) {
    super(serverMessage);
    this.name = 'ApiError';
  }

  get isRetryable(): boolean {
    return this.kind === 'busy' || this.kind === 'timeout' || this.kind === 'network';
  }
}

function classifyStatus(status: number): ApiError['kind'] {
  if (status === 429) return 'busy';
  if (status === 504) return 'timeout';
  if (status === 401 || status === 403) return 'auth';
  if (status >= 500) return 'server';
  return 'client';
}

async function apiErrorFromResponse(response: Response): Promise<ApiError> {
  let serverMessage = '';
  try {
    const raw = await response.text();
    try {
      const parsed = JSON.parse(raw);
      serverMessage = [parsed?.error, parsed?.details].filter(Boolean).join(' — ');
    } catch {
      serverMessage = raw.slice(0, 200);
    }
  } catch {
    // The status code still identifies the broad failure class.
  }
  const retryAfter = response.headers.get('Retry-After');
  const retryAfterSec = retryAfter !== null && !Number.isNaN(Number(retryAfter)) ? Number(retryAfter) : null;
  return new ApiError(response.status, serverMessage || `Server returned HTTP ${response.status}`, retryAfterSec, classifyStatus(response.status));
}

export async function fetchPreview(
  moduleId: string,
  params: Record<string, number>,
  options?: { signal?: AbortSignal; group?: number | string; analyze?: boolean; quality?: 'low' | 'medium' | 'high' },
): Promise<any> {
  const signal = options?.signal ?? newPreviewController().signal;
  const requestParams: Record<string, number> = { ...params };
  try {
    const revision = sessionStorage.getItem('boxed3d_preview_revision');
    if (revision) requestParams.__previewRevision = Number(revision);
  } catch {
    // Preview remains available when session storage is disabled.
  }
  const payload = JSON.stringify({ moduleId, parameters: requestParams, quality: options?.quality ?? 'medium', group: options?.group ?? -1, analyze: options?.analyze === true });

  const request = async (baseUrl: string): Promise<any> => {
    let response: Response;
    try {
      response = await fetch(`${baseUrl}/api/preview`, {
        method: 'POST',
        headers: apiAuthHeaders({ 'Content-Type': 'application/json' }),
        body: payload,
        signal,
      });
    } catch (error) {
      if ((error as Error).name === 'AbortError') throw error;
      throw new ApiError(0, `Cannot reach the 3D API server (${baseUrl})`, null, 'network');
    }
    if (!response.ok) throw await apiErrorFromResponse(response);
    const data = await response.json();
    const mesh = data.mesh ?? data;
    if (typeof mesh === 'object' && mesh !== null) {
      if (data.generatedAt) mesh.generatedAt = data.generatedAt;
      if (data.engine) mesh.engine = data.engine;
      if (data.metaTimeMs !== undefined) mesh.metaTimeMs = data.metaTimeMs;
      if (data.metaTime) mesh.metaTime = data.metaTime;
    }
    return mesh;
  };

  try {
    return await request(API_BASE);
  } catch (error) {
    if ((error as Error).name === 'AbortError') return null;
    if (!(error instanceof ApiError) || error.kind !== 'network') throw error;
    const alternateBase = API_BASE.includes('localhost')
      ? API_BASE.replace('localhost', '127.0.0.1')
      : API_BASE.replace('127.0.0.1', 'localhost');
    try {
      return await request(alternateBase);
    } catch (alternateError) {
      if ((alternateError as Error).name === 'AbortError') return null;
      throw alternateError;
    }
  }
}
